import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2 } from 'lucide-react';
import { earlyAccessApi } from '../lib/api';

/**
 * "Join the waiting list" email capture, used on the coming-soon landing
 * hero and on the blocked /register page. Always shows the same success
 * message whether or not the address was already on the list.
 */
export const EarlyAccessForm = ({ className = '', testIdPrefix = 'early-access' }) => {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await earlyAccessApi.registerInterest({ email, full_name: fullName || null });
      setDone(true);
    } catch {
      // The backend only fails this on a malformed address; the message
      // covers that without implying anything about the list itself.
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={`rounded-lg bg-brand-wash text-brand-ink px-5 py-4 ${className}`} data-testid={`${testIdPrefix}-done`}>
        <p className="font-heading text-[17px] font-semibold">You&apos;re on the list</p>
        <p className="text-[15px] mt-1">We&apos;ll email you when it&apos;s your turn.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`} data-testid={`${testIdPrefix}-form`}>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-email`} className="text-[15px] font-semibold block">
          What&apos;s your email address?
        </Label>
        <Input
          id={`${testIdPrefix}-email`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          required
          data-testid={`${testIdPrefix}-email-input`}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-name`} className="text-[15px] font-semibold block">
          Your name <span className="font-normal text-ink-55">(optional)</span>
        </Label>
        <Input
          id={`${testIdPrefix}-name`}
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          data-testid={`${testIdPrefix}-name-input`}
        />
      </div>
      <Button type="submit" disabled={loading || !email} className="w-full sm:w-auto" data-testid={`${testIdPrefix}-submit-btn`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join the waiting list'}
      </Button>
    </form>
  );
};

export default EarlyAccessForm;
